/**
 * Email Page Component
 *
 * A standalone page for testing email functionality via Resend API.
 * This page is publicly accessible (no authentication required).
 */

import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
  VStack,
  Link,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { EmailForm, TemplateEmailForm, EmailStatus } from '../../components/email';

const EmailPage: React.FC = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="3xl">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Heading size="xl" mb={2}>
              Email Service
            </Heading>
            <Text color="gray.600">
              Send emails using the Resend API
            </Text>
            <HStack justify="center" mt={2} spacing={4}>
              <Link href="/" color="blue.500" fontSize="sm">
                Back to Home
              </Link>
              <Link href="/home" color="blue.500" fontSize="sm">
                Go to App
              </Link>
            </HStack>
          </Box>

          {/* Email Forms */}
          <Box bg={cardBg} borderRadius="lg" shadow="md" p={6}>
            <Tabs variant="enclosed" colorScheme="blue">
              <TabList>
                <Tab>Send Email</Tab>
                <Tab>Template Email</Tab>
                <Tab>Check Status</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <EmailForm />
                </TabPanel>
                <TabPanel>
                  <TemplateEmailForm />
                </TabPanel>
                <TabPanel>
                  <EmailStatus />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>

          {/* Info */}
          <Box bg={cardBg} borderRadius="lg" shadow="md" p={6}>
            <Heading size="sm" mb={3}>
              API Endpoints
            </Heading>
            <VStack align="stretch" spacing={2} fontSize="sm" fontFamily="mono">
              <Text>POST /api/email/send - Send HTML/text email</Text>
              <Text>POST /api/email/send-template - Send template email</Text>
              <Text>GET /api/email/status/:id - Check email status</Text>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default EmailPage;
